import React, { Fragment } from 'react'
import { Tab } from 'react-bootstrap'
import { validationConfigWeb, validationConfigServer, validationConfigSpa, validationConfigMobile } from './validationConfig'
import CustomCollapse from '../../CustomCollapse'
import CustomTabs from '../../CustomTabs'
import { SelectField, TextField, TextFieldWithCopy, ToggleCheckbox } from '../../FormFields'
import CheckIcon from '../../../../assets/icons/check-icon.svg'
import ErrorIcon from '../../../../assets/icons/exclamation-icon.svg'
import FormValidators from '../../../../helpers/FormValidators'
// constants
import ClientTypesConstants, { ClientTypeTitle } from '../../../../constants/ClientTypes'
import AccessTokenTypesConstants from '../../../../constants/AccessTokenTypes'
import ReleaseStatus from '../../../../constants/ReleaseStatus'
import ClientStatus from '../../../../constants/ClientStatus'
import ClientUrlTypesConstants from '../../../../constants/ClientUrlTypesConstants'
import SecretTypesConstants from '../../../../constants/SecretTypes'
// components
import ClientEmptyState from './components/ClientEmptyState'
import RemoveClientButton from './components/RemoveClientButton'
import ClientUrls from './components/ClientUrls'
import CopyClientScopes from './components/CopyClientScopes'
import GenerateCertificate from './components/GenerateCertificate'
import UpdateClientSecret from './components/UpdateClientSecret'

const ClientsForm = ({ formStateWeb, formStateServer, formStateSpa, formStateMobile, appScopes, releaseId, releaseStatus, disabled }) => {

  //  ===== Methods =====
  const getFormStateByClientType = (clientType) => {
    switch (clientType) {
      case ClientTypesConstants.WEB: return formStateWeb
      case ClientTypesConstants.SERVER: return formStateServer
      case ClientTypesConstants.SPA: return formStateSpa
      case ClientTypesConstants.MOBILE: return formStateMobile
      default: return null
    }
  }

  const getValidationConfigByClientType = (clientType) => {
    switch (clientType) {
      case ClientTypesConstants.WEB: return validationConfigWeb
      case ClientTypesConstants.SERVER: return validationConfigServer
      case ClientTypesConstants.SPA: return validationConfigSpa
      case ClientTypesConstants.MOBILE: return validationConfigMobile
      default: return null
    }
  }

  const onChange = (key, value, clientType) => {
    const { formData, onFormDataChanged, validateFormField } = getFormStateByClientType(clientType)
    const validationConfig = getValidationConfigByClientType(clientType)

    // Handling special senario for "isDisabled" key
    if (key === "isDisabled") {
      value = value === true ? ClientStatus.INACTIVE : ClientStatus.ACTIVE
      key = "status"
    }

    // Handling special senario for "redirectUrls", "corsUrls", "logoutUrls" keys
    if (key === "redirectUrls") {
      value = getUpdatedClientUrlsArray(ClientUrlTypesConstants.REDIRECT, value, formData)
      key = "clientUrls"
    }
    if (key === "corsUrls") {
      value = getUpdatedClientUrlsArray(ClientUrlTypesConstants.CORS, value, formData)
      key = "clientUrls"
    }
    if (key === "logoutUrls") {
      value = getUpdatedClientUrlsArray(ClientUrlTypesConstants.LOGOUT, value, formData)
      key = "clientUrls"
    }

    onFormDataChanged(key, value)
    validateFormField(key, value, validationConfig)
  }

  const getUpdatedClientUrlsArray = (clientUrlType, value, formData) => {
    const urls = value?.map(val => ({
      type: clientUrlType,
      applicationClientId: formData?.id,
      id: val.id,
      value: val.value,
      inputId: val.inputId
    }))
    const filteredUrls = formData?.clientUrls?.filter(url => url.type !== clientUrlType)
    return [...filteredUrls, ...urls]
  }

  const onAddClientClicked = (clientType) => {
    const { setFormData } = getFormStateByClientType(clientType)
    setFormData({
      clientType: clientType,
      clientUrls: [],
      secretType: getClientSecretType(clientType)
    })
  }

  const getClientSecretType = (clientType) => {
    switch (clientType) {
      case ClientTypesConstants.WEB:
        return SecretTypesConstants.SHARED_SECRET
      case ClientTypesConstants.SERVER:
        return SecretTypesConstants.X509_CERTIFICATE_BASE64
      default:
        return null
    }
  }

  const onRemoveClientClicked = (clientType) => {
    const { setFormData } = getFormStateByClientType(clientType)
    setFormData(null)
  }

  const checkAllClientFormsValidity = () => {
    const { formErrors: formErrorsWeb } = formStateWeb
    const { formErrors: formErrorsServer } = formStateServer
    const { formErrors: formErrorsSpa } = formStateSpa
    const { formErrors: formErrorsMobile } = formStateMobile

    const isWebFormValid = FormValidators.checkFormValidity(formErrorsWeb, validationConfigWeb)
    const isServerFormValid = FormValidators.checkFormValidity(formErrorsServer, validationConfigServer)
    const isSpaFormValid = FormValidators.checkFormValidity(formErrorsSpa, validationConfigSpa)
    const isMobileFormValid = FormValidators.checkFormValidity(formErrorsMobile, validationConfigMobile)

    return isWebFormValid && isServerFormValid && isSpaFormValid && isMobileFormValid
  }

  const renderClientForm = (clientType) => {
    const { formData, formErrors } = getFormStateByClientType(clientType)

    if (!formData) {
      return (
        <ClientEmptyState
          clientType={clientType}
          onAddClientClicked={() => onAddClientClicked(clientType)}
          disabled={disabled}
        />
      )
    }

    return (
      <Fragment>
        {formData["clientId"] && releaseStatus === ReleaseStatus.LIVE && (
          <TextFieldWithCopy
            displayName="Client ID"
            value={formData["clientId"]}
            disabled={true}
          />
        )}
        {formData["clientId"] && (
          <CopyClientScopes
            clientType={clientType}
            appScopes={appScopes}
          />
        )}
        <TextField
          type="number"
          displayName="Access Token Lifetime"
          onChange={(value) => onChange("accessTokenLifeTime", value, clientType)}
          errorMessage={formErrors["accessTokenLifeTime"]?.message}
          value={formData["accessTokenLifeTime"]}
          disabled={formData["status"] === ClientStatus.INACTIVE || disabled}
        />
        <SelectField
          displayName="Access Token Type"
          placeholder="Select access token type"
          onChange={(value) => onChange("accessTokenType", value, clientType)}
          options={[{ value: AccessTokenTypesConstants.JWT, displayName: 'JWT' }]}
          errorMessage={formErrors["accessTokenType"]?.message}
          value={formData["accessTokenType"]}
          disabled={formData["status"] === ClientStatus.INACTIVE || disabled}
        />
        {clientType !== ClientTypesConstants.SERVER && formData["clientId"] && (
          <ToggleCheckbox
            displayName="Require Consent"
            value={formData["requireConsent"]}
            disabled={true}
          />
        )}
        {clientType !== ClientTypesConstants.SERVER && (
          <ClientUrls
            formData={formData}
            formErrors={formErrors}
            onChange={(key, value) => onChange(key, value, clientType)}
            disabled={formData["status"] === ClientStatus.INACTIVE || disabled}
            clientType={clientType}
          />
        )}
        {clientType === ClientTypesConstants.WEB && releaseStatus === ReleaseStatus.LIVE && (
          <UpdateClientSecret
            releaseId={releaseId}
            clientId={formData["clientId"]}
          />
        )}
        {clientType === ClientTypesConstants.SERVER && releaseStatus === ReleaseStatus.LIVE && (
          <GenerateCertificate
            releaseId={releaseId}
            clientId={formData["clientId"]}
          />
        )}
        {formData["clientId"] && releaseStatus !== ReleaseStatus.PENDING && (
          <ToggleCheckbox
            displayName={`Disable ${ClientTypeTitle(clientType)} client`}
            onChange={(value) => onChange("isDisabled", value, clientType)}
            value={formData["status"] === ClientStatus.INACTIVE}
            disabled={disabled}
          />
        )}
        {!!formData["clientId"] || (
          <RemoveClientButton
            clientType={clientType}
            onRemoveClientClicked={() => onRemoveClientClicked(clientType)}
            disabled={disabled}
          />
        )}
      </Fragment>
    )
  }

  return (
    <CustomCollapse
      title="Clients"
      icon={checkAllClientFormsValidity() ? CheckIcon : ErrorIcon}
    >
      <CustomTabs defaultActiveKey="webclient">
        <Tab eventKey="webclient" title="Web Client">
          {renderClientForm(ClientTypesConstants.WEB)}
        </Tab>
        <Tab eventKey="serverclient" title="Server Client">
          {renderClientForm(ClientTypesConstants.SERVER)}
        </Tab>
        <Tab eventKey="spaclient" title="SPA Client">
          {renderClientForm(ClientTypesConstants.SPA)}
        </Tab>
        <Tab eventKey="mobileclient" title="Mobile Client">
          {renderClientForm(ClientTypesConstants.MOBILE)}
        </Tab>
      </CustomTabs>
    </CustomCollapse>
  )
}

export default ClientsForm
